/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

import { PrintOptionFilterOptionDto } from '@/api-sdk';
import { FgFullscreenImage } from '@/components';
import { iconFullColor, iconStopwatch } from '@/components/icons';
import { MobileContext } from '@/context';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles } from '@/styles';

import '@/components/image/fg-image';

@customElement('fg-print-option-filter-option')
export class FgPrintOptionFilterOption extends LitElement {
  @property({ type: Number })
  subStepIndex: number = 0;

  @property({ type: Object })
  option?: PrintOptionFilterOptionDto;

  @consume({ context: MobileContext, subscribe: true })
  isMobile: boolean = false;

  fullscreenImageDialogRef: Ref<FgFullscreenImage> | null = null;

  render() {
    this.fullscreenImageDialogRef = createRef();

    if (!this.option) {
      return null;
    }

    const {
      name,
      thumbnailImage,
      mainImage,
      description,
      queryString,
      hspAvailable,
      isFullColor,
      isMostChosen,
    } = this.option;

    return html`
      <button class="option" @click=${() => this.selectOption(queryString)}>
        ${thumbnailImage &&
        html`
          <div class="option-image">
            <fg-image
              src=${thumbnailImage}
              width="64"
              height="64"
              @click=${(event: Event) => {
                event.stopPropagation();
                this.fullscreenImageDialogRef?.value?.open(event);
              }}
            ></fg-image>
          </div>
        `}
        <fg-fullscreen-image
          class="fullscreen-button"
          .imageUrl=${mainImage}
          ${ref(this.fullscreenImageDialogRef)}
          .hasZoomInCursor=${!this.isMobile}
        ></fg-fullscreen-image>
        <div>
          <strong class="option-label">${name}</strong>
          ${description &&
          html`
            <span class="option-description">${description}</span>
          `}
          ${isFullColor
            ? html`
                <span class="option-full-color">
                  ${iconFullColor} ${t('labels.fullColor')}
                </span>
              `
            : nothing}
          ${hspAvailable
            ? html`
                <span class="option-hsp">
                  ${iconStopwatch} ${t('labels.deliveredQuickly')}
                </span>
              `
            : nothing}
        </div>
        ${isMostChosen
          ? html`
              <span class="most-chosen-label">${t('labels.mostChosen')}</span>
            `
          : nothing}
      </button>
    `;
  }

  selectOption(queryString: PrintOptionFilterOptionDto['queryString']) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString,
            code: this.option?.code,
            subStepIndex: this.subStepIndex,
          },
        },
      }),
    );
  }

  static styles = [
    baseStyles,
    css`
      .option {
        position: relative;
        height: 100%;
        min-height: 80px;
        background: #fff;
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        padding: 8px;
      }

      .option:hover {
        color: var(--fg-primary, var(--fg-fallback-primary));
        border-color: var(--fg-primary, var(--fg-fallback-primary));
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }

      .option-image {
        flex: 0 0 64px;
      }

      .option-image fg-image::part(image) {
        width: 100%;
        object-fit: contain;
        aspect-ratio: 64 / 64;
      }

      .option-label {
        font-size: 1rem;
        line-height: 1.6;
      }

      .option-description {
        font-style: italic;
        line-height: 1.4;
        display: block;
      }

      .option-hsp {
        display: flex;
        align-items: center;
        margin-top: 0.25rem;
        gap: 3px;
      }

      .fullscreen-button {
        position: absolute;
        top: 12px;
        left: 12px;
        color: var(--fg-primary, var(--fg-fallback-primary));
      }

      .most-chosen-label {
        position: absolute;
        right: -5px;
        top: -10px;
        padding: 4px 8px;
        border-radius: 24px;
        background-color: var(
          --fg-success--light,
          var(--fg-fallback-success--light)
        );
        font-weight: 600;
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
      }

      .option-full-color {
        display: flex;
        align-items: center;
        gap: 3px;
        margin-left: 6px;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .fullscreen-button {
          visibility: hidden;
        }
        .option:hover .fullscreen-button {
          visibility: visible;
        }

        .option-image:hover {
          cursor: zoom-in;
        }
      }
    `,
  ];
}
